import { useState, useEffect, useCallback } from 'react'
import { CaselawReferenceMetadataResponseV3 } from '../schema/caselaw-reference-viewer.schema'
import * as Sentry from '@sentry/browser'
import { nanoid } from 'nanoid'
import { getCaselawMetadataForStandaloneViewerV3 } from '../fetch/get-caselaw-metadata-for-standalone-viewer-v3'
import { CaselawStorageField } from '@/common/types/caselaw-identifiers'
import { ApiError } from '@/util/errors/api-error'

interface UseCaselawReferenceState {
  data: CaselawReferenceMetadataResponseV3 | null
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

/**
 * Use Caselaw Metadata
 * Fetches caselaw metadata from the V3 endpoint.
 * @param idType - The type of identifier (s3_dir or s3_path)
 * @param id - The identifier
 * @returns The caselaw metadata for the given idType and id
 */
export function useCaselawMetadataInStandalone(idType: string, id: string): UseCaselawReferenceState {
  const [data, setData] = useState<CaselawReferenceMetadataResponseV3 | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)

  if (!id) {
    setData(null)
    setIsError(true)
    throw new Error('No reference found')
  }

  const fetchData = useCallback(async () => {
    setIsLoading(true)
    setIsError(false)

    try {
      let result

      switch (idType) {
        case CaselawStorageField.S3_DIR:
        case CaselawStorageField.S3_PATH:
          result = await getCaselawMetadataForStandaloneViewerV3(id)
          break
        default:
          throw new Error(`Unrecognized idType "${idType}". Expected s3_dir or s3_path.`)
      }

      if (!result) {
        setIsError(true)
        setData(null)
        throw new Error(`No result found for idType "${idType}" and id "${id}".`)
      }

      setData(result)
      setIsError(false)
    } catch (err) {
      if (!(err instanceof ApiError)) {
        const sentry_transaction_id = nanoid()
        Sentry.withScope((scope) => {
          scope.setTags({ transaction_id: sentry_transaction_id })

          Sentry.captureException(new Error(`Caselaw metadata fetch error - detail: ${err}`), {
            extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled, idType: idType, id: id },
          })
        })
      }
      setIsError(true)
      setData(null)
    } finally {
      setIsLoading(false)
    }
  }, [idType, id])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    data,
    isLoading,
    isError,
    refetch: fetchData,
  }
}
